import React from 'react';
import AnalyticsTable from '../../../components/AnalyticsTable/AnalyticsTable';
import { COMPARISON_TABLE, SHIFT_TABLE } from '../../../utils/analyticsTableParams';
import Sparkline from './Sparkline';
import {
  describeDelta, formatCountCell, formatHoursCell, formatPercentCell, formatRateCell,
} from '../summaryFormatters';
import { VIOLATION_VOCAB } from '../summaryVocab';

/*
 * ANALYTICS-TABLES01 (ТЗ v4 §25–26, FE-FIX22/FE-FIX23).
 *
 * «Сравнение объектов и локаций» — строка = Объект + Локация
 * (rankings.object_locations), «Рейтинг смен» — строка = Объект + Локация +
 * Камера + Смена (rankings.camera_shifts). Обе таблицы серверные: порядок,
 * метрики, total и применение смен/таймзон — backend. Фронт не суммирует
 * строки камер и не пересчитывает rate.
 *
 * Одноимённые локации в разных объектах и одноимённые смены на разных камерах
 * не смешиваются, потому что каждая строка несёт свой parent-контекст.
 */

/*
 * Числовая ячейка в две строки: текущее значение и под ним серым «было X» со
 * знаком изменения. Нет прошлого значения — «—», это не ноль.
 * good = 'down': рост плох (нарушения, на 100 ч); good = 'up': рост хорош;
 * good = 'none': оценки нет (бизнес-события).
 */
const ComparisonCell = ({ current, previous, format, good }) => {
  const delta = describeDelta(current, previous, good);
  return (
    <span className="sum-cmp-cell">
      <span>{current == null ? '—' : format(current)}</span>
      <span className="sum-cmp-prev">
        {previous != null ? `было ${format(previous)}` : '—'}
        {delta && <span className={`sum-delta sum-delta--${delta.tone}`}>{delta.text}</span>}
      </span>
    </span>
  );
};

const countColumn = (vocab) => ({
  key: 'count',
  label: vocab.countTitle,
  sortKey: vocab.countKey,
  numeric: true,
  compact: true,
  render: (row) => (
    <ComparisonCell
      current={row[vocab.countKey]}
      previous={row[vocab.previousCountKey]}
      format={formatCountCell}
      good={vocab.growthIs}
    />
  ),
});

// Подпись крупного графика — строка таблицы: «Босфор / Цех розлива / C110 / Дневная».
const rowTitle = (row) => [row.object_name, row.location_name, row.camera_name, row.shift_name]
  .filter(Boolean).join(' / ');

const trendColumn = (timeZone) => ({
  key: 'trend',
  label: 'Тренд',
  compact: true,
  render: (row) => (
    <Sparkline
      points={row.trend_points}
      previousPoints={row.previous_trend_points}
      title={rowTitle(row)}
      timeZone={timeZone}
    />
  ),
});

const metricColumns = (vocab) => [
  {
    key: 'rate',
    label: 'На 100 ч',
    sortKey: vocab.rateKey,
    numeric: true,
    compact: true,
    render: (row) => (
      <ComparisonCell
        current={row[vocab.rateKey]}
        previous={row[vocab.previousRateKey]}
        format={formatRateCell}
        good={vocab.growthIs}
      />
    ),
  },
  {
    key: 'hours',
    label: 'Часы контроля',
    sortKey: 'available_control_hours',
    numeric: true,
    compact: true,
    render: (row) => (
      <ComparisonCell
        current={row.available_control_hours}
        previous={row.previous_available_control_hours}
        format={formatHoursCell}
        good="up"
      />
    ),
  },
  {
    key: 'continuity',
    label: 'Непрерывность',
    sortKey: 'continuity_percent',
    numeric: true,
    compact: true,
    // Без часов контроля непрерывность не определена, 0% вводил бы в заблуждение.
    render: (row) => (
      <ComparisonCell
        current={Number(row.available_control_hours) > 0 ? row.continuity_percent : null}
        previous={Number(row.previous_available_control_hours) > 0 ? row.previous_continuity_percent : null}
        format={formatPercentCell}
        good="up"
      />
    ),
  },
];

// Колонки строятся один раз на словарь и пояс: AnalyticsTable сравнивает их по ссылке.
const columnsCache = new Map();
const buildColumns = (vocab, timeZone) => {
  const byZone = columnsCache.get(vocab) || new Map();
  columnsCache.set(vocab, byZone);
  if (byZone.has(timeZone)) return byZone.get(timeZone);
  const columns = {
    comparison: [
      { key: 'rank', label: '#', compact: true, render: (row) => row.rank },
      { key: 'object', label: 'Объект', sortKey: 'object_name', render: (row) => row.object_name },
      { key: 'location', label: 'Локация', sortKey: 'location_name', render: (row) => row.location_name },
      countColumn(vocab),
      trendColumn(timeZone),
      ...metricColumns(vocab),
    ],
    shift: [
      { key: 'rank', label: '#', compact: true, render: (row) => row.rank },
      { key: 'object', label: 'Объект', sortKey: 'object_name', render: (row) => row.object_name },
      { key: 'location', label: 'Локация', sortKey: 'location_name', render: (row) => row.location_name },
      { key: 'camera', label: 'Камера', sortKey: 'camera_name', render: (row) => row.camera_name },
      { key: 'shift', label: 'Смена', sortKey: 'shift_name', render: (row) => row.shift_name },
      countColumn(vocab),
      trendColumn(timeZone),
      ...metricColumns(vocab),
    ],
  };
  byZone.set(timeZone, columns);
  return columns;
};

const RankingsSection = ({ rankings, scope, tables, disabled, vocab = VIOLATION_VOCAB, trendTimezone }) => {
  const columns = buildColumns(vocab, trendTimezone);
  const objectLocations = rankings?.object_locations;
  const cameraShifts = rankings?.camera_shifts;
  if (!objectLocations && !cameraShifts) return null;

  return (
    <>
      {objectLocations && (
        <AnalyticsTable
          table={tables.comparison.table || COMPARISON_TABLE}
          state={tables.comparison.state}
          onChange={tables.comparison.onChange}
          payload={objectLocations}
          columns={columns.comparison}
          rowKey={(row) => `${row.object_id}-${row.location_id}`}
          title="Сравнение объектов и локаций"
          scope={scope}
          disabled={disabled}
          emptyText="Локаций с данными за период нет"
          idPrefix="sum-comparison"
          resizable={false}
          size="large"
        />
      )}

      {cameraShifts && (
        <AnalyticsTable
          table={tables.shift.table || SHIFT_TABLE}
          state={tables.shift.state}
          onChange={tables.shift.onChange}
          payload={cameraShifts}
          columns={columns.shift}
          rowKey={(row) => `${row.camera_id}-${row.shift_key}`}
          title="Рейтинг смен по камерам"
          scope={scope}
          disabled={disabled}
          emptyText="Смен с данными за период нет"
          idPrefix="sum-shift"
          resizable={false}
          size="large"
        />
      )}
    </>
  );
};

export default RankingsSection;
